//@flow
import * as React from 'react';
import { SizeEnum } from 'common/enums/UIEnums';
import * as Immutable from 'immutable';
import { compose } from 'recompose';
import {
    Box,
    Text,
    Column,
    Columns,
    Stack,
    Icon as NeoIcon
} from '@locus-taxy/neo-ui';

import { collapsibleListCardFactory } from 'common/components/core/card/CollapsibleListCard';
import Checkbox from 'common/components/core/checkbox/';
import SearchInput from 'common/components/core/search_input';
import Loader from 'common/components/core/loader/Loader';
import { neoUIColors } from 'common/enums/ColorEnum';
import { translate, type TFunction } from 'react-i18next';
import type {
    TerritoryZoneType,
    DistributorOutletObject
} from './TerritoryPlanningContainer';
import { type ThemeType } from 'react-css-themr';

type Props = {
    t: TFunction,
    selectedZones: Array<TerritoryZoneType>,
    distributorSelectionError: ?string,
    areDistributorOutletsLoading: boolean,
    distributorSearchInput: ?string,
    onSearchInputChange: (input: string) => void,
    theme: ThemeType,
    selectDistributorToggle: () => void,
    onDistributorToggle: (distributor: string, checked: boolean) => void,
    onPageChange: () => void,
    distributorOutletMap: ?Immutable.Map<string, DistributorOutletObject>,
    allDistributors: ?Array<string>,
    isExpandible: boolean,
    isExpanded: boolean,
    areDistributorOutletsLoading: boolean
};

type DistributorCardProps = {
    onDistributorToggle: () => void,
    theme: ThemeType,
    areDistributorOutletsLoading: () => void,
    t: TFunction
};
class DistributorCard extends React.PureComponent<DistributorCardProps> {
    render() {
        const {
            onDistributorToggle,
            theme,
            areDistributorOutletsLoading,
            t
        } = this.props;
        return (
            <Box
                width="fluid"
                height="5vh"
                p={3}
                onClick={onDistributorToggle}
                className={theme.distributorHeader}
            >
                <Columns space="xSmall" alignY="center">
                    <Column width="fluid">
                        <Text fontWeight={600} fontSize={3}>
                            {t('entityManagement:territory.distributors')}
                        </Text>
                    </Column>
                    <Loader
                        theme={theme}
                        size={SizeEnum.Small}
                        active={areDistributorOutletsLoading}
                    />
                </Columns>
            </Box>
        );
    }
}

type DistributorItemProps = {
    selectedZones: Array<TerritoryZoneType>,
    distributorSelectionError: ?string,
    theme: ThemeType,
    distributorOutletMap: Map<string, Object>,
    selectDistributorToggle: (distributor: string, checked: boolean) => void,
    distributor: string,
    t: TFunction
};
class DistributorItem extends React.PureComponent<DistributorItemProps> {
    handleSelectToggle = checked => {
        const { distributor, selectDistributorToggle } = this.props;
        selectDistributorToggle(distributor, checked);
    };

    render() {
        const {
            distributor,
            distributorOutletMap,
            theme,
            distributorSelectionError,
            selectedZones
        } = this.props;
        const selectedZone = selectedZones.find(
            zone => zone.distributor === distributor
        );
        const color = selectedZone
            ? selectedZone.color
            : neoUIColors.grey500.code;
        const isChecked = distributorOutletMap.get(distributor)?.selected;
        return (
            <Box
                height="52px"
                p={3}
                className={isChecked ? theme.selectedItem : null}
            >
                <Columns alignY="center" space="small">
                    <Column width="content">
                        <NeoIcon
                            size="large"
                            icon="location_map"
                            color={color}
                        />
                    </Column>
                    <Column width="fluid">
                        <Text fontWeight={400} fontSize={3}>
                            {distributor}
                        </Text>
                    </Column>
                    <Column width="content">
                        <Checkbox
                            disabled={!isChecked && distributorSelectionError}
                            checked={isChecked}
                            smallCheckbox
                            onChange={this.handleSelectToggle}
                        />
                    </Column>
                </Columns>
            </Box>
        );
    }
}

class DistributorItems extends React.PureComponent<Props> {
    getContainer = () => {
        return 'allocatedOutlets';
    };

    onDistributorSearchChange = value => {
        const { onSearchInputChange } = this.props;
        onSearchInputChange(value);
    };

    render() {
        const {
            allDistributors,
            selectDistributorToggle,
            distributorOutletMap,
            theme,
            selectedZones,
            distributorSelectionError,
            areDistributorOutletsLoading,
            t
        } = this.props;
        if (areDistributorOutletsLoading) {
            return (
                <Box>
                    <Loader active />
                </Box>
            );
        }
        return (
            <Box height="35vh">
                <Stack space="small">
                    <Box height="3vh">
                        <Columns alignY="center" space="small">
                            <Column width="fluid">
                                <SearchInput
                                    onSearchInputChange={
                                        this.onDistributorSearchChange
                                    }
                                    theme={theme}
                                    placeholder={t(
                                        'entityManagement:territory.searchDistributor'
                                    )}
                                />
                            </Column>
                        </Columns>
                    </Box>
                    <Box height="33vh">
                        {distributorOutletMap &&
                            allDistributors?.map(distributor => {
                                return (
                                    <DistributorItem
                                        t={t}
                                        key={distributor}
                                        selectedZones={selectedZones}
                                        distributorSelectionError={
                                            distributorSelectionError
                                        }
                                        theme={theme}
                                        distributorOutletMap={
                                            distributorOutletMap
                                        }
                                        selectDistributorToggle={
                                            selectDistributorToggle
                                        }
                                        distributor={distributor}
                                    />
                                );
                            })}
                    </Box>
                </Stack>
            </Box>
        );
    }
}

const CollapsibleDistributorList = collapsibleListCardFactory({
    CardComponent: DistributorCard,
    CollapseComponent: DistributorItems
});

export default (compose(translate('entityManagement'))(
    CollapsibleDistributorList
): React.AbstractComponent<Props>);
